import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';

const Beranda = () => {
  const [beritaList, setBeritaList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Angka dihitung dari direktori publik (hanya anggota berstatus Disetujui)
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data, error } = await supabase
          .from('direktori_publik_view')
          .select('province_name');

        if (error) throw error;

        const rows = data || [];
        setStats({
          anggota: rows.length,
          provinsi: new Set(rows.map((row) => row.province_name).filter(Boolean)).size
        });
      } catch (err) {
        console.error('Error fetching statistik:', err);
      }
    };

    fetchStats();
  }, []);

  useEffect(() => {
    const fetchBerita = async () => {
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('berita')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) {
          throw error;
        }

        setBeritaList(data || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBerita();
  }, []);

  return (
    <div className="w-full">
      {/* 1. Hero / Banner Section */}
      <section className="relative bg-emerald-700 text-white py-24 px-4 sm:px-6 lg:px-8 flex items-center justify-center text-center overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div> {/* Placeholder for background image overlay */}
        <div className="relative z-10 max-w-3xl mx-auto flex flex-col items-center">
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4 leading-tight">
            Pemberdayaan Ekonomi Pesantren Nusantara
          </h1>
          <p className="text-lg md:text-xl text-emerald-100 mb-8 max-w-2xl">
            Bergabunglah bersama jaringan Himpunan Ekonomi Bisnis Pesantren (HEBITREN) untuk mewujudkan kemandirian ekonomi umat.
          </p>
          <Link to="/daftar" className="bg-white text-emerald-700 font-bold text-lg px-8 py-3 rounded-full shadow-lg hover:bg-emerald-50 transition duration-300">
            Daftar Sekarang
          </Link>
        </div>
      </section>

      {/* 2. Sambutan / Ringkasan Singkat */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white text-center">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-800 mb-6">Selamat Datang di HEBIPEDIA</h2>
          <p className="text-slate-600 leading-relaxed">
            HEBITREN (Himpunan Ekonomi Bisnis Pesantren) merupakan organisasi independen dan nonpartisan yang berfokus pada penguatan ekosistem ekonomi pesantren di Indonesia. Melalui kolaborasi, inovasi, dan pemanfaatan teknologi digital, HEBITREN mendorong kemandirian, produktivitas, serta daya saing pesantren dalam mendukung pembangunan ekonomi umat.
          </p>
        </div>
      </section>

      {/* 3. Statistik (Grid) */}
      <section className="py-16 bg-slate-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 max-w-xl mx-auto gap-6 text-center">
            {[
              { label: 'Pesantren Terdaftar', value: stats ? stats.anggota : '-' },
              { label: 'Provinsi', value: stats ? stats.provinsi : '-' }
            ].map((stat, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 flex flex-col items-center justify-center">
                <span className="text-4xl font-extrabold text-emerald-600 mb-2">{stat.value}</span>
                <span className="text-sm font-medium text-slate-500 uppercase tracking-wide">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Berita & Kegiatan Terbaru */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-10">
            <h2 className="text-3xl font-bold text-slate-800">Berita Terbaru</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {isLoading ? (
              <div className="col-span-1 md:col-span-3 text-center py-10 text-slate-500">Memuat berita...</div>
            ) : error ? (
              <div className="col-span-1 md:col-span-3 text-center py-10 text-red-500">Gagal memuat berita: {error}</div>
            ) : beritaList.length === 0 ? (
              <div className="col-span-1 md:col-span-3 text-center py-10 text-slate-500">Belum ada berita.</div>
            ) : (
              beritaList.map((item) => (
                <Link to={`/berita/${item.id}`} key={item.id || item.created_at} className="group flex flex-col bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow">
                  <div className="h-48 w-full bg-slate-200 flex items-center justify-center text-slate-400 overflow-hidden">
                    {item.gambar || item.image_url || item.gambar_url ? (
                      <img src={item.gambar || item.image_url || item.gambar_url} alt={item.judul || item.title || 'Berita'} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                    ) : (
                      <span>[Tanpa Gambar]</span>
                    )}
                  </div>
                  <div className="p-6 flex-grow flex flex-col">
                    <span className="text-xs text-emerald-600 font-bold mb-2">{item.kategori || 'UPDATE KEGIATAN'}</span>
                    <h3 className="text-lg font-bold text-slate-800 mb-3 group-hover:text-emerald-600 transition-colors line-clamp-2">
                      {item.judul || item.title || 'Judul Berita'}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 flex-grow line-clamp-3">
                      {item.deskripsi || item.konten || item.excerpt || item.isi_berita || item.content || 'Deskripsi berita...'}
                    </p>
                    <span className="text-xs text-slate-400 font-medium">
                      {item.created_at ? new Date(item.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' }) : 'Tanggal tidak tersedia'}
                    </span>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      {/* 5. Call to Action Bawah */}
      <section className="py-20 bg-emerald-50 px-4 sm:px-6 lg:px-8 text-center border-t border-emerald-100">
        <h2 className="text-2xl md:text-3xl font-bold text-emerald-900 mb-6">Siap Menjadi Bagian dari Ekosistem Kami?</h2>
        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
          <Link to="/daftar" className="bg-emerald-600 text-white font-bold px-8 py-3 rounded-md shadow-md hover:bg-emerald-800 w-full sm:w-auto transition-colors">
            Mulai Pendaftaran
          </Link>
          <Link to="/keanggotaan" className="bg-white text-emerald-600 font-bold px-8 py-3 rounded-md shadow-sm border-2 border-emerald-600 hover:bg-emerald-50 w-full sm:w-auto transition-colors">
            Pelajari Persyaratan
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Beranda;